import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';

import Btn from '../../components/ui/Btn';
import { Card, Input, Spinner } from '../../components/ui/primitives';
import { Icon } from '../../components/ui/Icon';
import Logo from '../../components/ui/Logo';
import { ErrorBanner } from '../auth/SignInScreen';
import { supabase } from '../../lib/supabase';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { useIsMobile } from '../../lib/useBreakpoint';

const WORKSPACE_TYPES = [
  { key: 'client',   label: 'Client onboarding', desc: 'Collect docs from clients', icon: Icon.users },
  { key: 'vendor',   label: 'Vendor compliance', desc: 'W-9s, COIs, NDAs',          icon: Icon.shield },
  { key: 'employee', label: 'Employee HR',       desc: 'Hiring paperwork',          icon: Icon.building },
];

const TEAM_SIZES = ['Just me', '2–5', '6–20', '21–50', '51+'];

function toSlug(str) {
  return (str || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .substring(0, 40);
}

export default function CreateTeamScreen() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isMobile = useIsMobile();

  const [name, setName] = useState('');
  const [types, setTypes] = useState(['client', 'vendor', 'employee']);  // default: all three selected
  const [size, setSize] = useState('2–5');
  const [language, setLanguage] = useState('en');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const slug = toSlug(name);
  const initials = name.trim()
    ? name.split(/\s+/).map(w => w[0]).slice(0, 2).join('').toUpperCase()
    : null;

  const submit = async () => {
    setError(null);

    if (!name.trim()) {
      setError('Please enter a workspace name.');
      return;
    }
    if (!slug) {
      setError('Workspace name must contain at least one letter or number.');
      return;
    }
    if (!user) {
      setError('You must be signed in to create a workspace.');
      return;
    }

    setSubmitting(true);
    try {
      // 1. Verify the user can create another team (free=1, pro=3, business=∞)
      const limit = await api('/api/check-team-limit');
      if (!limit.allowed) {
        throw new Error(limit.error || 'You\'ve reached your team limit.');
      }

      // 2. Insert the team
      const { data: team, error: teamError } = await supabase
        .from('teams')
        .insert({ name: name.trim(), slug, language_default: language })
        .select()
        .single();

      if (teamError) {
        if ((teamError.message || '').toLowerCase().includes('duplicate')) {
          throw new Error('That workspace name is already taken. Try a different one.');
        }
        throw new Error(teamError.message || 'Failed to create workspace.');
      }

      // 3. Get the auto-created Owner role
      const { data: roles, error: rolesError } = await supabase
        .from('team_roles')
        .select('id')
        .eq('team_id', team.id)
        .eq('slug', 'owner')
        .limit(1);

      if (rolesError || !roles?.length) {
        throw new Error('Workspace setup failed — owner role not found.');
      }

      // 4. Add the user as Owner
      const meta = user.user_metadata || {};
      const { error: memberError } = await supabase
        .from('team_members')
        .insert({
          team_id: team.id,
          user_id: user.id,
          role_id: roles[0].id,
          email: user.email,
          first_name: meta.first_name || '',
          last_name:  meta.last_name  || '',
          language,
          is_owner: true,
        });

      if (memberError) {
        throw new Error(memberError.message || 'Failed to add you to the workspace.');
      }

      // 5. Done — back to hub (it will pick up the new team)
      navigate('/hub');
    } catch (err) {
      console.error('Create team failed:', err);
      setError(err.message || 'Something went wrong creating your workspace.');
      setSubmitting(false);
    }
  };

  return (
    <div style={{ background: 'var(--bg)', minHeight: '100vh' }}>
      {/* Minimal top bar — full AppTopBar lands in Phase 2 */}
      <div style={{
        height: 56, padding: isMobile ? '0 16px' : '0 24px',
        borderBottom: '1px solid var(--border)',
        background: 'var(--surface)', display: 'flex', alignItems: 'center', gap: 16,
      }}>
        <Link to="/hub" style={{ textDecoration: 'none' }}>
          <Logo size={36} useFile />
        </Link>
      </div>

      <div style={{
        maxWidth: 720, margin: '0 auto',
        padding: isMobile ? '24px 16px' : '40px 32px',
      }}>
        <Link to="/hub" style={{ textDecoration: 'none' }}>
          <Btn variant="ghost" size="sm" icon={Icon.chevL} style={{ marginBottom: 16 }}>
            Back to workspaces
          </Btn>
        </Link>

        <h1 style={{ fontSize: 30, fontWeight: 700, letterSpacing: '-0.025em', marginBottom: 8 }}>
          Create a new workspace
        </h1>
        <p style={{ fontSize: 14, color: 'var(--text-3)', marginBottom: 32 }}>
          Workspaces are separate, isolated spaces — one per team, client group, or business unit.
        </p>

        {error && <ErrorBanner message={error} style={{ marginBottom: 16 }} />}

        <Card style={{ padding: isMobile ? 18 : 28 }}>
          {/* Logo placeholder + name input — stacks on mobile */}
          <div style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? 14 : 20,
            marginBottom: 24,
            alignItems: isMobile ? 'flex-start' : 'center',
          }}>
            <div style={{
              width: isMobile ? 64 : 80, height: isMobile ? 64 : 80, borderRadius: 18, flexShrink: 0,
              background: initials ? '#0a0f2e' : 'var(--surface-2)',
              color: '#3dc75b', display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: isMobile ? 22 : 28, fontWeight: 700, letterSpacing: '-0.02em',
              border: initials ? 'none' : '2px dashed var(--border-strong)',
            }}>
              {initials || <Icon.image size={isMobile ? 22 : 28} stroke="var(--text-muted)" />}
            </div>
            <div style={{ flex: 1, width: '100%' }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)', display: 'block', marginBottom: 6 }}>
                Workspace name
              </label>
              <Input
                icon={Icon.building}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Northwind Clinics, Acme HR"
                disabled={submitting}
              />
              <div style={{ fontSize: 11.5, color: 'var(--text-3)', marginTop: 6 }}>
                URL: <span style={{ color: 'var(--text-2)', fontFamily: 'var(--font-mono)' }}>
                  magicintake.com/{slug || '—'}
                </span>
              </div>
            </div>
          </div>

          {/* Workspace type */}
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)', display: 'block', marginBottom: 4 }}>
            What will this workspace be used for?
          </label>
          <div style={{ fontSize: 11.5, color: 'var(--text-3)', marginBottom: 10 }}>
            Pick one or more — you can always add more templates later.
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
            gap: 10, marginBottom: 20,
          }}>
            {WORKSPACE_TYPES.map((t) => {
              const sel = types.includes(t.key);
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => {
                    // Require at least one to stay selected
                    if (sel && types.length === 1) return;
                    setTypes(prev => sel ? prev.filter(k => k !== t.key) : [...prev, t.key]);
                  }}
                  disabled={submitting}
                  style={{
                    padding: 14, borderRadius: 10, cursor: 'pointer',
                    border: `1.5px solid ${sel ? 'var(--accent)' : 'var(--border)'}`,
                    background: sel ? 'var(--accent-bg)' : 'var(--surface)',
                    fontFamily: 'inherit', textAlign: 'left',
                    transition: 'border-color 0.12s, background 0.12s',
                    position: 'relative',
                  }}
                >
                  {sel && (
                    <div style={{
                      position: 'absolute', top: 10, right: 10,
                      width: 18, height: 18, borderRadius: '50%',
                      background: 'var(--accent)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <Icon.check size={11} stroke="#fff" strokeWidth={3} />
                    </div>
                  )}
                  <t.icon size={18} stroke={sel ? 'var(--accent-600)' : 'var(--text-2)'} />
                  <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 2, marginTop: 8, color: 'var(--text)' }}>
                    {t.label}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-3)' }}>{t.desc}</div>
                </button>
              );
            })}
          </div>

          {/* Team size — UI only for now, not persisted (no DB column yet) */}
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)', display: 'block', marginBottom: 6 }}>
            Team size
          </label>
          <div style={{
            display: isMobile ? 'grid' : 'flex',
            gridTemplateColumns: isMobile ? 'repeat(3, 1fr)' : undefined,
            gap: 6, marginBottom: 20,
          }}>
            {TEAM_SIZES.map((s) => {
              const sel = size === s;
              return (
                <button
                  key={s}
                  type="button"
                  onClick={() => setSize(s)}
                  disabled={submitting}
                  style={{
                    flex: isMobile ? undefined : 1,
                    padding: isMobile ? '10px 6px' : '8px 0',
                    fontSize: 12.5, fontWeight: 600,
                    border: `1px solid ${sel ? 'var(--accent)' : 'var(--border)'}`,
                    background: sel ? 'var(--accent-bg)' : 'var(--surface)',
                    color: sel ? 'var(--accent-600)' : 'var(--text-2)',
                    borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit',
                  }}
                >
                  {s}
                </button>
              );
            })}
          </div>

          {/* Default language */}
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)', display: 'block', marginBottom: 6 }}>
            Default language
          </label>
          <select
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
            disabled={submitting}
            className="mi-focusable"
            style={{
              width: '100%', height: 40, padding: '0 12px', marginBottom: 20,
              fontSize: 13.5, fontFamily: 'inherit', color: 'var(--text)',
              background: 'var(--surface)', border: '1px solid var(--border)',
              borderRadius: 'var(--r-md)', cursor: 'pointer',
            }}
          >
            <option value="en">English</option>
            <option value="es">Español</option>
          </select>

          {/* Pre-config hint */}
          <div style={{
            padding: 14, background: 'var(--surface-2)', borderRadius: 10,
            marginBottom: 20, display: 'flex', gap: 10, fontSize: 12, color: 'var(--text-2)',
          }}>
            <Icon.sparkle size={14} stroke="var(--accent-600)" style={{ flexShrink: 0, marginTop: 2 }} />
            <div>We'll pre-configure 3 common templates for you. You can customize them in minutes.</div>
          </div>

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Link to="/hub" style={{ textDecoration: 'none' }}>
              <Btn variant="outline" size="md" disabled={submitting}>Cancel</Btn>
            </Link>
            <Btn
              variant="primary"
              size="md"
              iconRight={submitting ? null : Icon.arrowR}
              onClick={submit}
              disabled={submitting || !name.trim()}
            >
              {submitting ? <><Spinner size={14} color="rgba(255,255,255,0.6)" /> Creating…</> : 'Create workspace'}
            </Btn>
          </div>
        </Card>
      </div>
    </div>
  );
}
